import React from 'react';
import gettext from 'js-utils/gettext';
import { SubmissionError } from 'redux-form';
import includes from 'lodash/includes';
import find from 'lodash/find';

import { trackEventFromState } from '@eb/site-analytics';
import {
    SIGN_IN_METHOD_FACEBOOK_LOGIN,
    SIGN_IN_METHOD_PASSWORD,
    SOCIAL_EXTERNAL_IDENTITY_PROVIDER_FACEBOOK,
} from '@eb/social-login-facebook';

import {
    sendResetPasswordEmail,
    sendSoftAccountActivationEmail,
    setAccountStatusChecked,
    updateFormType,
    showAuthNotification,
} from './auth';
import {
    GA_AUTH_CATEGORY,
    GA_ENTER_EMAIL_EVENT,
    GA_EXISTING_ACCT_LABEL,
    GA_SOFT_ACCT_LABEL,
    LOG_IN_PATH,
    GA_NEW_ACCT_LABEL,
    SIGN_UP_PATH,
    ACCOUNT_CONNECTED_TO_FACEBOOK_ERROR_MESSAGE,
    GA_GET_STARTED_EMAIL_ALREADY_CONNECTED_TO_FB,
    ONBOARDING_REDIRECT_URL_BLACKLIST,
} from '../constants';
import { isFacebookLoginEnabled, isLoginProviderEnabled } from '../utils';

import AlertChunkySvg from 'eventbrite_design_system/iconography/icons/AlertChunky';

export const getLogInFormData = (
    { password, captcha: recaptchaResponse, recaptchaV3ResponseToken },
    getState,
) => {
    const {
        authentication: { email, forward, referrer, pckg, stld },
    } = getState();

    return {
        password,
        email,
        forward,
        referrer,
        pckg,
        stld,
        recaptchaResponse,
        recaptchaV3ResponseToken,
    };
};

export const getSignUpFormData = (
    { firstName, lastName, password, captcha: recaptchaResponse },
    getState,
) => {
    const {
        authentication: { email, forward, referrer, userTypeParams, pckg },
    } = getState();

    return {
        password,
        firstName,
        lastName,
        recaptchaResponse,
        email,
        forward,
        referrer,
        ...userTypeParams,
        pckg,
    };
};

export const getSocialLogInFormData = (
    { accessToken, sourceProvider, skipCreation },
    getState,
) => {
    const {
        authentication: { forward, referrer, pckg },
    } = getState();

    return {
        accessToken,
        sourceProvider,
        skipCreation,
        forward,
        referrer,
        pckg,
    };
};

export const getSocialSignUpFormData = (
    { email, firstName, lastName },
    currentState,
) => {
    const skipCreation = false;
    const {
        authentication: {
            forward,
            referrer,
            pckg,
            socialData: { accessToken, sourceProvider },
        },
    } = currentState;

    return {
        accessToken,
        sourceProvider,
        skipCreation,
        email,
        firstName,
        lastName,
        forward,
        referrer,
        pckg,
    };
};

export const handleResetPasswordAccountStatusResponse = (
    dispatch,
    { exists, can_login: canLogin },
) => {
    let emailAction = sendResetPasswordEmail;

    if (exists && !canLogin) {
        emailAction = sendSoftAccountActivationEmail;
    }

    if (!exists && !canLogin) {
        throw new SubmissionError({ email: gettext('Unknown email provided') });
    }

    return dispatch(emailAction());
};

export const handleAccountStatusResponse = (
    response,
    dispatch,
    getState,
    email,
) => {
    const state = getState();
    const {
        authentication: { socialSSOSettings },
    } = state;
    const { user_id: userId, sign_in_methods: signInMethods } = response;

    /*
    The subsequent routes (login/signup/etc) expect email to be in state, and the
    account status to be checked, so need to update store before dealing with the response
    logic. When setting account status as checked, the email reducer is
    also listening and will update.
    */
    dispatch(setAccountStatusChecked(email));

    if (!!userId && includes(signInMethods, SIGN_IN_METHOD_PASSWORD)) {
        trackEventFromState(state, {
            action: GA_ENTER_EMAIL_EVENT,
            category: GA_AUTH_CATEGORY,
            label: GA_EXISTING_ACCT_LABEL,
        });
        dispatch(updateFormType(LOG_IN_PATH));
    } else if (
        isLoginProviderEnabled(
            socialSSOSettings,
            SOCIAL_EXTERNAL_IDENTITY_PROVIDER_FACEBOOK,
        ) &&
        !!userId &&
        includes(signInMethods, SIGN_IN_METHOD_FACEBOOK_LOGIN)
    ) {
        trackEventFromState(state, {
            action: GA_GET_STARTED_EMAIL_ALREADY_CONNECTED_TO_FB,
            category: GA_AUTH_CATEGORY,
        });
        dispatch(
            showAuthNotification({
                message: ACCOUNT_CONNECTED_TO_FACEBOOK_ERROR_MESSAGE,
                iconType: <AlertChunkySvg />,
            }),
        );
    } else if (userId) {
        trackEventFromState(state, {
            action: GA_ENTER_EMAIL_EVENT,
            category: GA_AUTH_CATEGORY,
            label: GA_SOFT_ACCT_LABEL,
        });
        dispatch(sendSoftAccountActivationEmail());
    } else {
        trackEventFromState(state, {
            action: GA_ENTER_EMAIL_EVENT,
            category: GA_AUTH_CATEGORY,
            label: GA_NEW_ACCT_LABEL,
        });
        dispatch(updateFormType(SIGN_UP_PATH));
    }
};

export const shouldOnboardingRedirect = (state) => {
    const onboardingFeatureFlag = state.authentication.hasConsumerOnboarding;
    const referrer = state.authentication.referrer;

    if (!onboardingFeatureFlag) {
        return false;
    }

    return !find(ONBOARDING_REDIRECT_URL_BLACKLIST, (blacklistedPath) =>
        referrer.includes(blacklistedPath),
    );
};
